import incomingMessage from '../sounds/incoming-message.mp3';

/**
 * The one sound the app makes.
 *
 * There were two before, and they did not match: chat messages got a
 * 880Hz sine wave synthesised on the spot in SocketContext, and the
 * notification bell played `/notification.mp3` out of the public folder.
 * Same event class, two different noises, and neither could be changed
 * without finding both. This is now the only place that makes a sound,
 * and the file is imported rather than fetched by path, so the bundler
 * fingerprints it and a new version cannot be served stale from cache.
 *
 * Call `playNotificationSound()` for anything arriving from outside --
 * a chat message, a notification.
 */

// One arrival, one ring. A chat message reaches a trainer twice -- as the
// message itself and as the bell notification about it -- and a server
// that repeats itself reaches everyone more often than that. Calls landing
// inside this window of a ring that has just started are that same
// arrival, and stay silent.
const RING_ONCE_MS = 2500;
let lastRungAt = 0;

// One element, built lazily. Constructing it at module scope would start
// a network request for the audio on first import, before anything has
// asked to hear it.
let element = null;

const audio = () => {
  if (element) return element;
  try {
    element = new Audio(incomingMessage);
    element.preload = 'auto';
    element.volume = 0.55;
  } catch {
    // No Audio constructor (SSR, a locked-down embed). Nothing to play.
    element = null;
  }
  return element;
};

/**
 * Play it. Safe to call from anywhere and as often as you like: several
 * calls for what is one arrival ring once (RING_ONCE_MS).
 */
export const playNotificationSound = () => {
  const el = audio();
  if (!el) return;
  const now = Date.now();
  if (now - lastRungAt < RING_ONCE_MS) return;
  lastRungAt = now;
  try {
    el.currentTime = 0;
    // Browsers block audio until the page has been interacted with at
    // least once. That is expected on a fresh tab, not an error worth
    // surfacing, so the rejection is swallowed.
    el.play?.().catch(() => {});
  } catch {
    /* nothing to do -- a sound that will not play is not worth a throw */
  }
};

/**
 * Let the browser fetch and decode the clip ahead of the first
 * notification, so the sound is not late the one time it matters.
 * Call it once somewhere that mounts early.
 */
export const primeNotificationSound = () => {
  const el = audio();
  if (el) el.load?.();
};

export default playNotificationSound;
